import type { PartialNonNullable } from './utilityTypes';

/**
 * Verifies that the value is not null or undefined.
 * @param value The value that will be verified
 * @returns True when the value is not null or undefined, false otherwise
 */
export function isNonNullable<T>(value: T): value is NonNullable<T> {
    return value !== null && value !== undefined;
}

/**
 * Assert that the value is not null or undefined. Throws an error if the value is null or undefined.
 * @param value The value that will be asserted
 * @param errorMessage The message of the error that may be thrown
 */
export function assertIsNonNullable<T>(value: T, errorMessage?: string): asserts value is NonNullable<T> {
    if (isNonNullable(value) === false) {
        throw new Error(errorMessage ?? `Expected value to be non-nullable, but was ${value}`);
    }
}

/**
 * Get a value that is not null or undefined. Throws an error if the value is null or undefined.
 * @param value The value that will be asserted and returned
 * @param errorMessage The message of the error that may be thrown
 * @returns The value that is not null or undefined
 */
export function nonNullable<T>(value: T | null | undefined, errorMessage?: string): T {
    assertIsNonNullable(value, errorMessage);

    return value;
}

/**
 * Verifies that all specified keys are present and not null or undefined
 * @param item The object that will be verified
 * @param keys The keys that will be verified
 * @returns True when all specified keys are present and not null or undefined, false otherwise
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isPartialNonNullable<T extends Record<string, any>, NonNullableKeys extends keyof T>(
    item: Partial<T>,
    keys: NonNullableKeys[]
): item is PartialNonNullable<T, NonNullableKeys> {
    return keys.every((key) => isNonNullable(item[key]));
}

/**
 * Assert that all specified keys are present and not null or undefined. Throws an error otherwise.
 * @param item The object that will be asserted
 * @param keys The keys that will be verified
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function assertIsPartialNonNullable<T extends Record<string, any>, NonNullableKeys extends keyof T>(
    item: Partial<T>,
    keys: NonNullableKeys[]
): asserts item is PartialNonNullable<T, NonNullableKeys> {
    for (const key of keys) {
        if (isNonNullable(item[key]) === false) {
            throw new Error(`Expected property '${String(key)}' to be non-nullable, but was ${item[key]}`);
        }
    }
}

/**
 * Get an object with the specified keys that are not null or undefined. Throws an error if assertion fails.
 * @param item The object that will be asserted
 * @param keys The keys that will be verified
 * @returns The object with only the specified keys that are not null or undefined
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function partialNonNullable<T extends Record<string, any>, NonNullableKeys extends keyof T>(
    item: Partial<T>,
    keys: NonNullableKeys[]
): PartialNonNullable<T, NonNullableKeys> {
    assertIsPartialNonNullable(item, keys);

    return item;
}
